'use client';

import React, { useState } from 'react';
import {
  MapPin,
  Plane,
  Clock,
  Phone,
  Car,
  Search,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { RENTAL_LOCATIONS } from '@/lib/data';

interface LocationsViewProps {
  onSelectLocationForBooking: (locationName: string) => void;
}

export function LocationsView({ onSelectLocationForBooking }: LocationsViewProps) {
  const [search, setSearch] = useState('');
  const [filterAirportOnly, setFilterAirportOnly] = useState(false);

  const filtered = RENTAL_LOCATIONS.filter((loc) => {
    if (search && !loc.city.toLowerCase().includes(search.toLowerCase()) && !loc.name.toLowerCase().includes(search.toLowerCase()) && !loc.code.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    if (filterAirportOnly && !loc.isAirport) return false;
    return true;
  });

  return (
    <div className="w-full bg-slate-50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="mb-8">
          <span className="text-xs font-black tracking-wider uppercase text-[var(--color-primary)] block mb-1">
            NATIONWIDE PICK-UP HUBS
          </span>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
            fleetava Rental Locations
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-xl">
            Pick up your vehicle conveniently at all major international airports or downtown branches with express curbside return and 24/7 key drop.
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            <input
              type="text"
              placeholder="Search by city (e.g. Miami, LA, NY)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-hidden focus:ring-1 focus:ring-amber-500"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700 self-start sm:self-auto">
            <input
              type="checkbox"
              checked={filterAirportOnly}
              onChange={(e) => setFilterAirportOnly(e.target.checked)}
              className="accent-[var(--color-primary)]"
            />
            <span>Airport Locations Only</span>
          </label>
        </div>

        {/* Locations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((loc) => (
            <div
              key={loc.id}
              className="bg-white rounded-2xl border border-slate-200/90 hover:border-slate-300 p-6 shadow-xs hover:shadow-lg transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 text-[var(--color-primary)] flex items-center justify-center font-black text-xs font-mono">
                      {loc.code}
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        {loc.city}, {loc.state}
                      </span>
                      <h3 className="text-base font-extrabold text-slate-900">
                        {loc.name}
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="space-y-2.5 text-xs text-slate-500 mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[var(--color-primary)] shrink-0 mt-0.5" />
                    <span>{loc.address}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
                    <span>{loc.hours}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
                    <span className="font-semibold text-slate-700">{loc.phone}</span>
                  </div>

                  <div className="flex items-center gap-2 text-emerald-600 font-semibold pt-1">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    <span>{loc.fleetCount} Vehicles in Hub Ready Today</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => onSelectLocationForBooking(`${loc.name} (${loc.code})`)}
                  className="w-full py-2.5 bg-slate-900 hover:bg-[var(--color-primary)] text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>Rent From This Location</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
